import React, { useEffect, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import AuthShell from "@/components/brand/AuthShell";
import { API_BASE_URL } from "@/lib/apiBase";
import { safeInviteReturn, withInviteReturn } from "@/lib/inviteReturn";

const Signup = () => {
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    password: "",
    invite_code: "",
  });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = safeInviteReturn(searchParams.get("return"));

  useEffect(() => {
    document.title = "Sign up | Circa";
    return () => {
      document.title = "Circa — Stay close to what matters";
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);
    setError("");

    try {
      const response = await fetch(`${API_BASE_URL}/auth/signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          first_name: formData.first_name,
          last_name: formData.last_name,
          email: formData.email,
          password: formData.password,
          invite_code: formData.invite_code,
          ...(returnTo ? { return_path: returnTo } : {}),
        }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        const message =
          (typeof data?.message === "string" && data.message) ||
          (Array.isArray(data?.message) && data.message[0]) ||
          "Signup failed. Please try again.";
        throw new Error(message);
      }

      setSuccess(true);
    } catch (err) {
      setError(err.message || "An error occurred during signup");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AuthShell
      title="Create your account"
      subtitle="Join Circa to stay close to your workplace and Circa Social."
      footer={
        <>
          <p>
            Already have an account?{" "}
            <Link
              to={withInviteReturn("/login", returnTo)}
              className="font-medium text-primary hover:underline"
            >
              Sign in
            </Link>
          </p>
          <p className="mt-3">
            <Link to="/about" className="font-medium text-primary hover:underline">
              About Circa
            </Link>
          </p>
        </>
      }
    >
      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {success && (
        <Alert className="mb-4">
          <AlertDescription>
            Check your email for a verification link to finish creating your
            account. Locally, the link is printed in the API logs when{" "}
            <code className="text-xs">MAIL_DRIVER=log</code>.
          </AlertDescription>
        </Alert>
      )}
      {!success ? (
        <form onSubmit={handleSubmit} className="space-y-5" noValidate>
          <div className="space-y-2">
            <Label htmlFor="invite_code">Invite code</Label>
            <Input
              type="text"
              id="invite_code"
              name="invite_code"
              autoComplete="off"
              value={formData.invite_code}
              onChange={handleChange}
              required
              disabled={isSubmitting}
              placeholder="Shared early-access code"
            />
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="first_name">First name</Label>
              <Input
                type="text"
                id="first_name"
                name="first_name"
                autoComplete="given-name"
                value={formData.first_name}
                onChange={handleChange}
                required
                disabled={isSubmitting}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="last_name">Last name</Label>
              <Input
                type="text"
                id="last_name"
                name="last_name"
                autoComplete="family-name"
                value={formData.last_name}
                onChange={handleChange}
                required
                disabled={isSubmitting}
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              type="email"
              id="email"
              name="email"
              autoComplete="email"
              inputMode="email"
              value={formData.email}
              onChange={handleChange}
              required
              disabled={isSubmitting}
              placeholder="you@company.com"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <div className="relative">
              <Input
                type={showPassword ? "text" : "password"}
                id="password"
                name="password"
                autoComplete="new-password"
                value={formData.password}
                onChange={handleChange}
                required
                minLength={10}
                disabled={isSubmitting}
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground"
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </button>
            </div>
            <p className="text-xs text-muted-foreground">At least 10 characters.</p>
          </div>
          <Button
            type="submit"
            className="w-full circa-glow"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Sending verification…" : "Continue"}
          </Button>
        </form>
      ) : (
        <Button
          type="button"
          variant="outline"
          className="w-full"
          onClick={() => navigate(withInviteReturn("/login", returnTo))}
        >
          Back to sign in
        </Button>
      )}
    </AuthShell>
  );
};

export default Signup;
